import React from 'react';
import { Dialog } from '@headlessui/react';
import { 
  X, 
  Calendar, 
  Clock, 
  DollarSign, 
  AlertCircle,
  Target,
  Tag,
  FolderOpen,
  TrendingUp
} from 'lucide-react';

const ProjectDetailsModal = ({ open, onClose, project }) => {
  if (!project) return null;

  const getStatusColor = (color) => {
    switch(color) {
      case 'green': return 'text-green-700';
      case 'yellow': return 'text-yellow-700';
      case 'orange': return 'text-orange-700';
      case 'red': return 'text-red-700';
      default: return 'text-gray-700';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      {/* Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        {/* Modal Panel */}
        <Dialog.Panel className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-[95vw] md:max-w-[700px] max-h-[90vh] overflow-hidden shadow-2xl transition-colors">
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-600 dark:to-blue-600 text-white p-6 relative transition-colors">
            <div className="flex items-center gap-3 mb-2">
              <div 
                className="w-4 h-4 rounded-full" 
                style={{ backgroundColor: project.color }}
              />
              <h2 className="text-2xl font-bold text-white transition-colors">{project.name}</h2>
              {project.is_default && (
                <span className="bg-white/20 dark:bg-white/20 text-white px-3 py-1 rounded-full text-xs font-semibold transition-colors">
                  Default
                </span>
              )}
            </div>
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold transition-colors ${
              project.status.color === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' :
              project.status.color === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800' :
              project.status.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800' :
              'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
            }`}>
              {project.status.label}
            </div>
            <button
              onClick={onClose}
              className="absolute top-6 right-6 text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6 space-y-6">
            
            {/* This Week Activity */}
            <section className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-xl p-5 transition-colors">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2 transition-colors">
                <TrendingUp className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                This Week's Activity
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400 transition-colors">Hours Logged</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-800 dark:text-gray-100 transition-colors">
                    {project.hours_this_week || 0}h
                  </div>
                </div>
                {project.earnings_this_week > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400 transition-colors">Earned</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-800 dark:text-gray-100 transition-colors">
                      ${project.earnings_this_week}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Budget Section */}
            {project.budget && (project.budget.time_hours > 0 || project.budget.money_amount > 0) && (
              <section>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2 transition-colors">
                  <Target className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                  Budget & Progress
                </h3>
                
                {project.budget.time_hours > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-3 border border-gray-200 dark:border-gray-600 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Time Budget</span>
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 transition-colors">
                        {project.spent.time_hours.toFixed(1)}h / {project.budget.time_hours}h
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3 overflow-hidden transition-colors">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          project.time_percent >= 100 ? 'bg-gradient-to-r from-red-500 to-red-600' :
                          project.time_percent >= 90 ? 'bg-gradient-to-r from-orange-500 to-red-500' :
                          project.time_percent >= 75 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                          'bg-gradient-to-r from-cyan-500 to-blue-500'
                        }`}
                        style={{ width: `${Math.min(project.time_percent, 100)}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 transition-colors">{project.time_percent}% used</div>
                  </div>
                )}

                {project.budget.money_amount > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Money Budget</span>
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 transition-colors">
                        ${project.spent.money_amount.toFixed(2)} / ${project.budget.money_amount}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3 overflow-hidden transition-colors">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          project.money_percent >= 100 ? 'bg-gradient-to-r from-red-500 to-red-600' :
                          project.money_percent >= 90 ? 'bg-gradient-to-r from-orange-500 to-red-500' :
                          project.money_percent >= 75 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                          'bg-gradient-to-r from-green-500 to-emerald-500'
                        }`}
                        style={{ width: `${Math.min(project.money_percent, 100)}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 transition-colors">{project.money_percent}% used</div>
                  </div>
                )}

                {project.hourly_rate && project.hourly_rate.amount > 0 && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 transition-colors">
                    <DollarSign className="w-4 h-4" />
                    <span>Hourly Rate: ${project.hourly_rate.amount}/hr {project.hourly_rate.currency}</span>
                  </div>
                )}
              </section>
            )}

            {/* Timeline Section */}
            {project.timeline && (project.timeline.start_date || project.timeline.due_date) && (
              <section>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2 transition-colors">
                  <Calendar className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                  Timeline
                </h3>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3 border border-gray-200 dark:border-gray-600 transition-colors">
                  {project.timeline.start_date && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400 transition-colors">Start Date</span>
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-100 transition-colors">
                        {new Date(project.timeline.start_date).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {project.timeline.due_date && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400 transition-colors">Due Date</span>
                      <span className={`text-sm font-medium transition-colors ${
                        project.days_until_due !== null && project.days_until_due < 0 ? 'text-red-600 dark:text-red-400' :
                        project.days_until_due !== null && project.days_until_due <= 3 ? 'text-orange-600 dark:text-orange-400' :
                        'text-gray-800 dark:text-gray-100'
                      }`}>
                        {new Date(project.timeline.due_date).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {project.days_until_due !== null && (
                    <div className="pt-2 border-t border-gray-200 dark:border-gray-600 transition-colors">
                      <span className={`text-sm font-semibold transition-colors ${
                        project.days_until_due < 0 ? 'text-red-600 dark:text-red-400' :
                        project.days_until_due <= 3 ? 'text-orange-600 dark:text-orange-400' :
                        project.days_until_due <= 7 ? 'text-yellow-700 dark:text-yellow-400' :
                        'text-gray-800 dark:text-gray-100'
                      }`}>
                        {project.days_until_due < 0 
                          ? `Overdue by ${Math.abs(project.days_until_due)} days`
                          : project.days_until_due === 0
                          ? 'Due today!'
                          : `${project.days_until_due} days until due`
                        }
                      </span>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Alerts Section */}
            {project.alerts && project.alerts.length > 0 && (
              <section>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2 transition-colors">
                  <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  Alerts
                </h3>
                <div className="space-y-2">
                  {project.alerts.map((alert, index) => (
                    <div 
                      key={index} 
                      className="bg-orange-50 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 p-3 rounded-lg border border-orange-200 dark:border-orange-800 text-sm transition-colors"
                    >
                      {alert}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Tags & Category */}
            {((project.tags && project.tags.length > 0) || project.category) && (
              <section>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2 transition-colors">
                  <Tag className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                  Organization
                </h3>
                <div className="space-y-3">
                  {project.category && (
                    <div className="flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400 transition-colors">Category:</span>
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-100 transition-colors">{project.category}</span>
                    </div>
                  )}
                  {project.tags && project.tags.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Tag className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400 transition-colors">Tags:</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {project.tags.map((tag, index) => (
                          <span 
                            key={index}
                            className="px-3 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded-full text-xs font-medium border border-cyan-200 dark:border-cyan-800 transition-colors"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}

          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 transition-colors">
            <button
              onClick={onClose}
              className="w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-600 dark:to-blue-600 text-white font-semibold rounded-lg hover:from-cyan-700 hover:to-blue-700 transition-all"
            >
              Close
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default ProjectDetailsModal;
